import React, { useState, useEffect, useContext } from 'react'
import { useParams } from "react-router-dom"
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'
import { isEmpty, head, find } from 'lodash'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import ApiService from '../../data/services/ApiService'
import { GeneralContext } from '../../context'
import Description from '../../components/Molla/ProductDescription'
import ProductDetail from '../../components/Molla/ProductDetail'
import Sticky from '../../components/Common/Sticky'
import Media from '../../components/Molla/Media'
import Breadcrumbs from '../../components/Common/Breadcrumb'
import { getImagesOfProduct } from '../../helpers/utils'

const Product = () => {
    const generalContext = useContext(GeneralContext)
    const { loading, setLoading, setCartItemsCount } = generalContext
    const { slug } = useParams()
    const [product, setProduct] = useState({})

    const [gallery, setGallery] = useState([])

    const [productQuantity, setProductQuantity] = useState(1)
    const [firstVariantValue, setFirstVariantValue] = useState('')
    const [secondVariantValue, setSecondVariantValue] = useState('')
    const [thirdVariantValue, setThirdVariantValue] = useState('')

    const [stock, setStock] = useState(0)

    const [cartAlertModal, setCartAlertModal] = useState(false)
    const toggleCartAlertModal = () => setCartAlertModal(!cartAlertModal)

    const getProduct = async () => {
        try {
            setLoading(true)
            const response = await ApiService.getProductBySlug(slug)
            if (!response.data.success) {
                setLoading(false)
                return false
            }

            const { product } = response.data
            const photos = getImagesOfProduct(
                product.gallery || []
            )

            setGallery(photos)
            setProduct(product)
        } catch (e) {
            if (e.response) {
                toastr.error(`${e.response.data.message}`, 'Error')
            } else {
                toastr.error(`${e}`, 'Error')
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        (async () => {
            await getProduct()
        })()
    }, []) // eslint-disable-line

    useEffect(() => {
        getStock()
    }, [product, firstVariantValue, secondVariantValue, thirdVariantValue]) // eslint-disable-line

    const shareOnWhatsApp = async () => {
        const productPublicUrl = `https://mitiendasocial.com/producto/`
        const shareText = `Este%20producto%20te%20va%20a%20encantar%20%F0%9F%A4%A9%F0%9F%A4%A9%20`

        window.analytics.track("Product Shared on Whatsapp", {
            product: {
                productName: product.name,
                productId: product.id,
                productCode: product.code,
                providerName: product.supplier.nombreComercial,
                family: product.family.name,
                category: product.category.name,
                subcategory: product.subcategory.name,
                retailPrice: product.retailPrice,
                description: product.description
            }
        })

        window.open(`https://api.whatsapp.com/send?text=${shareText}${productPublicUrl}${product.supplierCode}`)
    }

    const onChangeQuantity = (quantity) => {
        setProductQuantity(quantity)
    }

    const addToCartButtonDisabled = () => {
        if (!isEmpty(product)) {
            if (isEmpty(head(product.variants).firstOption.label) &&
                isEmpty(head(product.variants).secondOption.label) &&
                isEmpty(head(product.variants).thirdOption.label) &&
                stock > 0) {
                return false
            }

            if (!isEmpty(head(product.variants).firstOption.label)) {
                if (isEmpty(firstVariantValue.toString()) || firstVariantValue === "0") {
                    return true
                }
            }

            if (!isEmpty(head(product.variants).secondOption.label)) {
                if (isEmpty(secondVariantValue.toString()) || secondVariantValue === "0") {
                    return true
                }
            }

            if (!isEmpty(head(product.variants).thirdOption.label)) {
                if (isEmpty(thirdVariantValue.toString()) || thirdVariantValue === "0") {
                    return true
                }
            }

            if (product.status.codeName === 'NO_DISPONIBLE') {
                return true
            }

            if (stock === 0) {
                return true
            }
        }

        return false
    }

    const getStock = async () => {
        if (!isEmpty(product) && product.variants.length === 1) {
            setStock(product.variants[0].stock.currentStock)

            return
        }

        const variantSelected = await find(product.variants, {
            firstOption: {
                value: firstVariantValue.toString()
            },
            secondOption: {
                value: secondVariantValue.toString()
            },
            thirdOption: {
                value: thirdVariantValue.toString()
            }
        })

        if (!isEmpty(variantSelected)) {
            setStock(variantSelected.stock.currentStock)
        }
    }

    const addToCart = async () => {
        try {
            setLoading(true)
            const data = {
                productId: product.id,
                quantity: productQuantity,
                firstVariantValue,
                secondVariantValue,
                thirdVariantValue,
                variantDefaultId: head(product.variants).id,
            }

            const response = await ApiService.addToCart(data)

            // Set el numero de articulos que el usuario tiene en su carrito
            setCartItemsCount(response.data.itemsOnCart)
            setProductQuantity(1)
            setFirstVariantValue("")
            setSecondVariantValue("")
            setThirdVariantValue("")

            window.analytics.track("Product Added to Cart", {
                product: {
                    productName: product.name,
                    productId: product.id,
                    productCode: product.code,
                    providerName: product.supplier.nombreComercial,
                    family: product.family.name,
                    category: product.category.name,
                    subcategory: product.subcategory.name,
                    retailPrice: product.retailPrice,
                    description: product.description,
                    quantity: productQuantity,
                    firstVariantValue,
                    secondVariantValue,
                    thirdVariantValue,
                }
            })
            toastr.success("Producto agregado al carrito.", "¡Éxito!")
        } catch (e) {
            if (e.response) {
                if (e.response.data.action === 'CART_SUPPLIER_DIFFERENT') {
                    toggleCartAlertModal()
                } else {
                    toastr.error(`${e.response.data.message}`, 'Error')
                }
            } else {
                toastr.error(`${e}`, 'Error')
            }
        } finally {
            setLoading(false)
        }
    }

    const resetCart = async () => {
        try {
            toggleCartAlertModal()
            setLoading(true)
            const response = await ApiService.resetCart()
            if (response.data.success) {
                setCartItemsCount(response.data.itemsOnCart)
                addToCart()
            }
        } catch (error) {
            toastr.error(error.response.data.message, "Error")
        } finally {
            setLoading(false)
        }
    }

    return (
        <React.Fragment>
            {!isEmpty(product) && (
                <div
                    className="page-content"
                    style={{ backgroundColor: 'white' }}
                >
                    <Breadcrumbs
                        title="Productos"
                        breadcrumbItem={product && product.name}
                        parentLink={`/discovery`}
                    />
                    <div className="container">
                        <div className="product-details-top">
                            <div className="row">
                                <div className="col-md-6">
                                    {gallery && <Media product={product} gallery={gallery} />}
                                </div>

                                <div className="col-md-6">
                                    {product && (
                                        <ProductDetail
                                            code={product.supplierCode}
                                            product={product}
                                            quantity={productQuantity}
                                            onChangeQuantity={onChangeQuantity}
                                            showVariants
                                            variants={product.variants}
                                            firstVariantValue={firstVariantValue}
                                            secondVariantValue={secondVariantValue}
                                            thirdVariantValue={thirdVariantValue}
                                            onChangeFirstVariant={setFirstVariantValue}
                                            onChangeSecondVariant={setSecondVariantValue}
                                            onChangeThirdVariant={setThirdVariantValue}
                                            variantsDisabled={product.status.codeName === 'NO_DISPONIBLE'}
                                            actionShareProductButton={shareOnWhatsApp}
                                            shareProductText="Compartir"
                                            addToCartButtonText="Agregar al carrito"
                                            addToCartButtonAction={addToCart}
                                            addToCartButtonDisabled={addToCartButtonDisabled()}
                                            loading={loading}
                                            stock={stock}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        {!loading && <Description product={product} />}
                    </div>
                </div>
            )}
            <Sticky
                loading={loading}
                onlyMobile
                showFirstButton
                firstButtonText="Agregar al carrito"
                firstButtonIcon={<i className="bx bx-cart"></i>}
                firstButtonAction={addToCart}
                firstButtonDisabled={addToCartButtonDisabled()}
                showSecondButton
                secondButtonText="Compartir"
                secondButtonIcon={<i className="bx bxl-whatsapp"></i>}
                secondButtonClass="btn-whatsapp"
                secondButtonAction={shareOnWhatsApp}
            />
            <div>
                <Modal isOpen={cartAlertModal} toggle={toggleCartAlertModal}>
                    <ModalHeader toggle={toggleCartAlertModal}>Producto de otro proveedor</ModalHeader>
                    <ModalBody>
                        Estás agregando un producto de un proveedor diferente, si deseas continuar, vamos a resetear tu carrito.
                    </ModalBody>
                    <ModalFooter>
                        <Button className="btn btn-default btn-rounded waves-effect waves-light mb-2 mr-2" onClick={toggleCartAlertModal}>Cancelar</Button>{' '}
                        <Button color="primary" className="btn btn-success btn-rounded waves-effect waves-light mb-2 mr-2" onClick={resetCart}>Resetear carrito</Button>
                    </ModalFooter>
                </Modal>
            </div>
        </React.Fragment>
    )
}

export default Product
